import type { ReactNode } from 'react';
import type { ReactElement } from 'react';
import { getApiErrorMessage } from '@quoruum/frontend-common/api/errorMessage';

export type PaginatedTableColumn<TRow> = {
  key: string;
  header: ReactNode;
  headerClassName?: string;
  cellClassName?: string;
  renderCell: (row: TRow) => ReactNode;
};

type PaginatedTableProps<TRow> = {
  columns: ReadonlyArray<PaginatedTableColumn<TRow>>;
  rows: ReadonlyArray<TRow>;
  getRowKey: (row: TRow, index: number) => string;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  loadingText: string;
  errorText: string;
  emptyText: string;
  page: number;
  hasNextPage: boolean;
  isFetchingPage?: boolean;
  onPreviousPage: () => void;
  onNextPage: () => void;
  pageSize: number;
  pageSizeOptions: ReadonlyArray<number>;
  onPageSizeChange: (nextPageSize: number) => void;
  rowClassName?: string;
};

export function PaginatedTable<TRow>({
  columns,
  rows,
  getRowKey,
  isLoading,
  isError,
  error,
  loadingText,
  errorText,
  emptyText,
  page,
  hasNextPage,
  isFetchingPage = false,
  onPreviousPage,
  onNextPage,
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
  rowClassName = '',
}: PaginatedTableProps<TRow>): ReactElement {
  if (isLoading) {
    return <p className="px-3 py-2 text-xs text-slate-600">{loadingText}</p>;
  }

  if (isError) {
    return (
      <p className="px-3 py-2 text-xs text-rose-700">{getApiErrorMessage(error, errorText)}</p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200 text-sm">
        <thead className="bg-slate-50">
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className={`px-3 py-2 text-left font-semibold text-slate-700 ${column.headerClassName ?? ''}`.trim()}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 bg-white">
          {rows.length > 0 ? (
            rows.map((row, index) => (
              <tr
                key={getRowKey(row, index)}
                className={`transition-colors ${rowClassName}`.trim()}
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={`px-3 py-2 text-slate-700 ${column.cellClassName ?? ''}`.trim()}
                  >
                    {column.renderCell(row)}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={Math.max(1, columns.length)}
                className="px-3 py-3 text-xs text-slate-600"
              >
                {emptyText}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-3 py-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onPreviousPage}
            disabled={page <= 1 || isFetchingPage}
            className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={onNextPage}
            disabled={!hasNextPage || isFetchingPage}
            className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Next
          </button>
          <span className="text-xs text-slate-600">Page {page}</span>
        </div>

        <label className="flex items-center gap-2 text-xs text-slate-700">
          Page size
          <select
            value={pageSize}
            onChange={(event) => {
              onPageSizeChange(Number(event.target.value));
            }}
            className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-900 outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500"
          >
            {pageSizeOptions.map((pageSizeOption) => (
              <option key={pageSizeOption} value={pageSizeOption}>
                {pageSizeOption}
              </option>
            ))}
          </select>
        </label>
      </div>
    </div>
  );
}
